/**
 * Shared notification event types used across Email Templates and Notification Rules
 * Keep this in sync with backend NotificationEventType enum
 */

export interface NotificationEventType {
  value: string;
  label: string;
  entityType: string;
}

export const NOTIFICATION_EVENT_TYPES: NotificationEventType[] = [
  // Case Events
  { value: 'CASE_CREATED', label: 'Case Created', entityType: 'CASE' },
  { value: 'CASE_ASSIGNED', label: 'Case Assigned', entityType: 'CASE' },
  { value: 'CASE_REASSIGNED', label: 'Case Reassigned', entityType: 'CASE' },
  { value: 'CASE_STATE_CHANGED', label: 'Case State Changed', entityType: 'CASE' },
  { value: 'CASE_WORK_NOTE_ADDED', label: 'Case Work Note Added', entityType: 'CASE' },
  { value: 'CASE_WORK_NOTE_INTERNAL', label: 'Case Work Note Added (Internal)', entityType: 'CASE' },
  { value: 'CASE_COMMENT_ADDED', label: 'Case Comment Added', entityType: 'CASE' },
  { value: 'CASE_RESOLVED', label: 'Case Resolved', entityType: 'CASE' },
  { value: 'CASE_CLOSED', label: 'Case Closed', entityType: 'CASE' },
  { value: 'CASE_CANCELLED', label: 'Case Cancelled', entityType: 'CASE' },
  { value: 'CASE_REOPENED', label: 'Case Reopened', entityType: 'CASE' },
  { value: 'CASE_ON_HOLD', label: 'Case On Hold', entityType: 'CASE' },
  { value: 'CASE_AWAITING_INFO', label: 'Case Awaiting Info', entityType: 'CASE' },
  { value: 'CASE_SOLUTION_PROPOSED', label: 'Case Solution Proposed', entityType: 'CASE' },

  // Incident Events
  { value: 'INCIDENT_CREATED', label: 'Incident Created', entityType: 'INCIDENT' },
  { value: 'INCIDENT_ASSIGNED', label: 'Incident Assigned', entityType: 'INCIDENT' },
  { value: 'INCIDENT_REASSIGNED', label: 'Incident Reassigned', entityType: 'INCIDENT' },
  { value: 'INCIDENT_ON_HOLD', label: 'Incident On Hold', entityType: 'INCIDENT' },
  { value: 'INCIDENT_CANCELLED', label: 'Incident Cancelled', entityType: 'INCIDENT' },
  { value: 'INCIDENT_REOPENED', label: 'Incident Reopened', entityType: 'INCIDENT' },
  { value: 'INCIDENT_RESOLVED', label: 'Incident Resolved', entityType: 'INCIDENT' },
  { value: 'INCIDENT_CLOSED', label: 'Incident Closed', entityType: 'INCIDENT' },
  { value: 'INCIDENT_WORK_NOTE_ADDED', label: 'Incident Work Note Added', entityType: 'INCIDENT' },
  { value: 'INCIDENT_WORK_NOTE_INTERNAL', label: 'Incident Work Note Added (Internal)', entityType: 'INCIDENT' },

  // Problem Events
  { value: 'PROBLEM_CREATED', label: 'Problem Created', entityType: 'PROBLEM' },
  { value: 'PROBLEM_ASSIGNED', label: 'Problem Assigned', entityType: 'PROBLEM' },
  { value: 'PROBLEM_REASSIGNED', label: 'Problem Reassigned', entityType: 'PROBLEM' },
  { value: 'PROBLEM_STATE_CHANGED', label: 'Problem State Changed', entityType: 'PROBLEM' },
  { value: 'PROBLEM_RESOLVED', label: 'Problem Resolved', entityType: 'PROBLEM' },
  { value: 'PROBLEM_CLOSED', label: 'Problem Closed', entityType: 'PROBLEM' },
  { value: 'PROBLEM_CANCELLED', label: 'Problem Cancelled', entityType: 'PROBLEM' },
  { value: 'PROBLEM_WORK_NOTE_ADDED', label: 'Problem Work Note Added', entityType: 'PROBLEM' },
  { value: 'PROBLEM_WORK_NOTE_INTERNAL', label: 'Problem Work Note Added (Internal)', entityType: 'PROBLEM' },
  { value: 'PROBLEM_CUSTOMER_APPROVAL_REQUEST', label: 'Problem Customer Approval Request', entityType: 'PROBLEM' },
  { value: 'PROBLEM_CUSTOMER_APPROVED', label: 'Problem Customer Approved', entityType: 'PROBLEM' },
  { value: 'PROBLEM_CUSTOMER_REJECTED', label: 'Problem Customer Rejected', entityType: 'PROBLEM' },

  // Change Events
  { value: 'CHANGE_CREATED', label: 'Change Created', entityType: 'CHANGE' },
  { value: 'CHANGE_ASSIGNED', label: 'Change Assigned', entityType: 'CHANGE' },
  { value: 'CHANGE_REASSIGNED', label: 'Change Reassigned', entityType: 'CHANGE' },
  { value: 'CHANGE_STATE_CHANGED', label: 'Change State Changed', entityType: 'CHANGE' },
  { value: 'CHANGE_APPROVED', label: 'Change Approved', entityType: 'CHANGE' },
  { value: 'CHANGE_REJECTED', label: 'Change Rejected', entityType: 'CHANGE' },
  { value: 'CHANGE_IMPLEMENTED', label: 'Change Implemented', entityType: 'CHANGE' },
  { value: 'CHANGE_CLOSED', label: 'Change Closed', entityType: 'CHANGE' },
  { value: 'CHANGE_ASSESSMENT_REQUEST', label: 'Change Assessment Request', entityType: 'CHANGE' },
  { value: 'CHANGE_APPROVAL_REQUEST', label: 'Change Approval Request', entityType: 'CHANGE' },
  { value: 'CHANGE_NOTES_ADDED', label: 'Change Notes Added', entityType: 'CHANGE' },
  { value: 'CHANGE_CANCELLED', label: 'Change Cancelled', entityType: 'CHANGE' },
  { value: 'CHANGE_AUTHORIZE', label: 'Change Authorize', entityType: 'CHANGE' },
  { value: 'CHANGE_CAB_APPROVED', label: 'Change CAB Approved', entityType: 'CHANGE' },
  { value: 'CHANGE_CAB_REJECTED', label: 'Change CAB Rejected', entityType: 'CHANGE' },
  { value: 'CHANGE_CUSTOMER_APPROVED', label: 'Change Customer Approved', entityType: 'CHANGE' },
  { value: 'CHANGE_CUSTOMER_REJECTED', label: 'Change Customer Rejected', entityType: 'CHANGE' },
  { value: 'CHANGE_REVIEW_DONE', label: 'Change Review Done', entityType: 'CHANGE' },

  // Service Request Events
  { value: 'SERVICE_REQUEST_CREATED', label: 'Service Request Created', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_ASSIGNED', label: 'Service Request Assigned', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_REASSIGNED', label: 'Service Request Reassigned', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_RESOLVED', label: 'Service Request Resolved', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_COMPLETED', label: 'Service Request Completed', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_ON_HOLD', label: 'Service Request On Hold', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_CANCELLED', label: 'Service Request Cancelled', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_REOPENED', label: 'Service Request Reopened', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_WORK_NOTE_ADDED', label: 'Service Request Work Note Added', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_WORK_NOTE_INTERNAL', label: 'Service Request Work Note Added (Internal)', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_APPROVED', label: 'Service Request Approved', entityType: 'SERVICE_REQUEST' },
  { value: 'SERVICE_REQUEST_REJECTED', label: 'Service Request Rejected', entityType: 'SERVICE_REQUEST' },

  // Alert Events
  { value: 'ALERT_CREATED', label: 'Alert Created', entityType: 'ALERT' },
  { value: 'ALERT_ACKNOWLEDGED', label: 'Alert Acknowledged', entityType: 'ALERT' },
  { value: 'ALERT_ASSIGNED', label: 'Alert Assigned', entityType: 'ALERT' },
  { value: 'ALERT_RESOLVED', label: 'Alert Resolved', entityType: 'ALERT' },

  // Knowledge Events
  { value: 'KNOWLEDGE_SENT_FOR_REVIEW', label: 'Knowledge Sent For Review', entityType: 'KNOWLEDGE' },
  { value: 'KNOWLEDGE_REVIEWED', label: 'Knowledge Reviewed', entityType: 'KNOWLEDGE' },
  { value: 'KNOWLEDGE_PUBLISHED', label: 'Knowledge Published', entityType: 'KNOWLEDGE' },
  { value: 'KNOWLEDGE_REJECTED', label: 'Knowledge Rejected', entityType: 'KNOWLEDGE' },
  { value: 'KNOWLEDGE_RETIRED', label: 'Knowledge Retired', entityType: 'KNOWLEDGE' },

  // Time Entry Events
  { value: 'TIME_ENTRY_REJECTED', label: 'Time Entry Rejected', entityType: 'TIME_ENTRY' },

  // SLA Breach Events - Available for all ticket types
  { value: 'SLA_RESPONSE_BREACHED', label: 'SLA Response Breached', entityType: 'INCIDENT' },
  { value: 'SLA_ONGOING_BREACHED', label: 'SLA Ongoing Breached', entityType: 'INCIDENT' },
  { value: 'SLA_RESOLUTION_BREACHED', label: 'SLA Resolution Breached', entityType: 'INCIDENT' },
  { value: 'SLA_RESPONSE_BREACHED', label: 'SLA Response Breached', entityType: 'CASE' },
  { value: 'SLA_ONGOING_BREACHED', label: 'SLA Ongoing Breached', entityType: 'CASE' },
  { value: 'SLA_RESOLUTION_BREACHED', label: 'SLA Resolution Breached', entityType: 'CASE' },
  { value: 'SLA_RESPONSE_BREACHED', label: 'SLA Response Breached', entityType: 'PROBLEM' },
  { value: 'SLA_ONGOING_BREACHED', label: 'SLA Ongoing Breached', entityType: 'PROBLEM' },
  { value: 'SLA_RESOLUTION_BREACHED', label: 'SLA Resolution Breached', entityType: 'PROBLEM' },
  { value: 'SLA_RESPONSE_BREACHED', label: 'SLA Response Breached', entityType: 'CHANGE' },
  { value: 'SLA_ONGOING_BREACHED', label: 'SLA Ongoing Breached', entityType: 'CHANGE' },
  { value: 'SLA_RESOLUTION_BREACHED', label: 'SLA Resolution Breached', entityType: 'CHANGE' },
  { value: 'SLA_RESPONSE_BREACHED', label: 'SLA Response Breached', entityType: 'SERVICE_REQUEST' },
  { value: 'SLA_ONGOING_BREACHED', label: 'SLA Ongoing Breached', entityType: 'SERVICE_REQUEST' },
  { value: 'SLA_RESOLUTION_BREACHED', label: 'SLA Resolution Breached', entityType: 'SERVICE_REQUEST' },
];
